// Adapted from https://ui.shadcn.com/docs/components/toast
import { useEffect, useState } from "react";

export type ToastProps = {
  title?: string;
  description?: string;
  variant?: "default" | "destructive" | "success";
  duration?: number;
};

export type Toast = ToastProps & {
  id: string;
  open: boolean;
};

export type ToasterToast = Toast;

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000;

const toasts: ToasterToast[] = [];

// Simple notification system
let listeners: Array<(toasts: ToasterToast[]) => void> = [];

function emitChange() {
  listeners.forEach((listener) => {
    listener([...toasts]);
  });
}

function addToast(toast: ToastProps) {
  const id = Math.random().toString(36).substring(2, 9);

  toasts.push({
    ...toast,
    id,
    open: true,
    duration: toast.duration || 3000,
  });

  if (toasts.length > TOAST_LIMIT) {
    toasts.shift();
  }

  emitChange();

  // Auto-dismiss the toast after its duration
  if (toast.duration !== Infinity) {
    setTimeout(() => {
      dismissToast(id);
    }, toast.duration || 3000);
  }

  return id;
}

function dismissToast(toastId: string) {
  const index = toasts.findIndex((toast) => toast.id === toastId);

  if (index !== -1) {
    toasts[index].open = false;
    emitChange();
  }

  setTimeout(() => {
    const index = toasts.findIndex((toast) => toast.id === toastId);
    if (index !== -1) {
      toasts.splice(index, 1);
      emitChange();
    }
  }, TOAST_REMOVE_DELAY);
}

export function useToast() {
  const [state, setState] = useState<ToasterToast[]>([]);

  useEffect(() => {
    const listener = (newToasts: ToasterToast[]) => {
      setState(newToasts);
    };

    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  return {
    toasts: state,
    toast: addToast,
    dismiss: dismissToast,
  };
}

// Simple export for direct use without the hook
export const toast = (props: ToastProps) => {
  return addToast(props);
};
